import { useState, useRef } from "react";
import {
  useV1ProductionRoutingWorkInstructionRevisionsCreate as useWorkInstructionRevisionCreate,
  useV1ProductionRoutingWorkInstructionRevisionsPublishCreate as useWorkInstructionRevisionPublish,
} from "../../../api/generated/v1/v1";
import { uploadCustomFieldMedia } from "../../customFields/api";

interface WorkInstructionRevisionEditorProps {
  familyId: string;
  onPublished?: () => void;
}

export function WorkInstructionRevisionEditor({
  familyId,
  onPublished,
}: WorkInstructionRevisionEditorProps) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [draftId, setDraftId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createRevision = useWorkInstructionRevisionCreate();
  const publishRevision = useWorkInstructionRevisionPublish();

  const handleSaveDraft = () => {
    createRevision.mutate(
      { data: { family: familyId, title, content, attachment_url: attachmentUrl } as any },
      {
        onSuccess: (res: any) => setDraftId(res.data?.id ?? res.id),
      }
    );
  };

  const handlePublish = () => {
    if (!draftId) return;
    publishRevision.mutate(
      { id: draftId, data: { family: familyId, title, content, attachment_url: attachmentUrl } as any },
      { onSuccess: () => onPublished?.() }
    );
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadError(null);
    try {
      const res = await uploadCustomFieldMedia(file);
      if (res?.id) {
        setAttachmentUrl(`/api/v1/custom-field-media/${res.id}/download/`);
        setUploadedFileName(file.name);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("Upload error:", err);
      setUploadError("خطا در آپلود فایل: " + msg);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div
      style={{
        marginTop: "1.5rem",
        padding: "1.5rem",
        backgroundColor: "#F9FAFB",
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "1.25rem",
      }}
    >
      <h4 style={{ margin: 0, color: "#007270", fontWeight: 700, fontSize: "1.1rem" }}>
        ویرایش متن دستورالعمل کاری
      </h4>
      
      <div>
        <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: 600, color: "#374151" }}>عنوان دستورالعمل</label>
        <input
          style={{ width: "100%", padding: "0.75rem", borderRadius: "8px", border: "1px solid #D1D5DB", outline: "none" }}
          placeholder="مثال: روش کار با دستگاه پرس"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div>
        <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: 600, color: "#374151" }}>محتوا و شرح عملیات</label>
        <textarea
          style={{ width: "100%", padding: "0.75rem", borderRadius: "8px", border: "1px solid #D1D5DB", minHeight: "160px", outline: "none", resize: "vertical" }}
          placeholder="توضیحات مربوط به ایستگاه را اینجا وارد کنید..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
      </div>

      <div>
        <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: 600, color: "#374151" }}>فایل ضمیمه (PDF یا عکس)</label>
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
          <input
            style={{ flex: 1, minWidth: "200px", padding: "0.75rem", borderRadius: "8px", border: "1px solid #D1D5DB", outline: "none", direction: "ltr" }}
            placeholder="لینک فایل یا آپلود از سیستم..."
            value={attachmentUrl}
            onChange={(e) => {
              setAttachmentUrl(e.target.value);
              setUploadedFileName(null);
            }}
          />
          <input 
            type="file" 
            ref={fileInputRef}
            style={{ display: "none" }} 
            onChange={handleFileUpload}
            accept=".pdf,image/*"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            style={{
              padding: "0.75rem 1.5rem",
              backgroundColor: "#D4AF37",
              color: "#FFFFFF",
              border: "none",
              borderRadius: "8px",
              fontWeight: 700,
              cursor: isUploading ? "not-allowed" : "pointer",
              opacity: isUploading ? 0.7 : 1,
              whiteSpace: "nowrap"
            }}
          >
            {isUploading ? "در حال آپلود..." : "آپلود فایل سیستم"}
          </button>
        </div>
        
        {/* Preview / Success state */}
        {uploadError && (
          <div style={{ marginTop: "0.75rem", padding: "0.75rem", backgroundColor: "#FEF2F2", borderRadius: "8px", border: "1px solid #F87171", color: "#DC2626", fontSize: "0.9rem" }}>
            {uploadError}
          </div>
        )}
        {attachmentUrl && !uploadError && (
          <div style={{ marginTop: "0.75rem", padding: "0.75rem", backgroundColor: "#ECFDF5", borderRadius: "8px", border: "1px solid #6EE7B7", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ color: "#059669", fontWeight: 600 }}>✓ فایل با موفقیت دریافت شد:</span>
            <a href={attachmentUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#007270", textDecoration: "underline", direction: "ltr", display: "inline-block" }}>
              {uploadedFileName || "مشاهده فایل پیوست"}
            </a>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem" }}>
        <button
          type="button"
          onClick={handleSaveDraft}
          disabled={createRevision.isPending || !title.trim()}
          style={{
            padding: "0.75rem 2rem",
            backgroundColor: "#E5E7EB",
            color: "#374151",
            border: "none",
            borderRadius: "8px",
            fontWeight: 700,
            cursor: (createRevision.isPending || !title.trim()) ? "not-allowed" : "pointer",
            opacity: (createRevision.isPending || !title.trim()) ? 0.5 : 1
          }}
        >
          ذخیره پیش‌نویس
        </button>
        <button
          type="button"
          onClick={handlePublish}
          disabled={!draftId || publishRevision.isPending}
          style={{
            padding: "0.75rem 2rem",
            backgroundColor: "#007270",
            color: "#FFFFFF",
            border: "none",
            borderRadius: "8px",
            fontWeight: 700,
            cursor: (!draftId || publishRevision.isPending) ? "not-allowed" : "pointer",
            opacity: (!draftId || publishRevision.isPending) ? 0.5 : 1
          }}
        >
          انتشار نهایی (Publish)
        </button>
      </div>
    </div>
  );
}

export default WorkInstructionRevisionEditor;
